
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action, State } from "vuex-class"

// ---- Libraries ----
import _ from "lodash"

// ---- Types ----
import { Case, User, TimeZone } from "@/types"
import { UserStatus } from "@/types/userStatus"
import { CurrentUser, ExtendedPermissions } from "@/types/user"
import { TenantFeatures } from "@/types/tenantFeatures"

// ---- Components ----
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"
import UserImage from "@/components/UserImage.vue"
import SkeletonLoader from "../SkeletonLoader.vue"
import CaseList from "@/components/CaseList/CaseList.vue"

// ---- Services ----
import api from "@/services/api"
import apiUserStatus from "@/services/api_UserStatus"
import { UserStatusScheduleView, UserStatusScheduleRun } from "@/types/userSchedules"
import { DateTime } from "luxon"

const caseStore = namespace("cases")
const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"case-list": CaseList,
		"skeleton": SkeletonLoader,
		"v-select": vSelect,
		"user-image": UserImage
	}
})
export default class TeamView extends Vue {
	// ---- Props ----

	@Prop()
	value: any;

	@Prop()
	edit!: boolean

	// ---- VueX ----

	@Getter("getMe") public me!: CurrentUser;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter("getAvailableUserStatuses") public availableUserStatuses!: UserStatus[];

	@Getter("getTenantTimezones") private readonly tenantTimeZones!: TimeZone[]

	@caseStore.Action("detailsLoad")
	private detailsLoad!: () => void;

	@Action("loadAvailableUserStatuses")
	private loadAvailableUserStatuses!: () => void;

	@Action("setCurrentUserStatus")
	private setCurrentUserStatus!: (userStatus: UserStatus) => void;

	@Action("clearCurrentUserStatus")
	private clearCurrentUserStatus!: () => void;

	@State
	teams!: any[];

	// ---- Data ----

	activeTeam: any = null;
	loading: boolean = true;
	users: User[] = []
	userSchedulesByUser: { [key: string]: UserStatusScheduleRun[] } = {}

	scheduleLookaheadOptions = [
		{
			value: null,
			label: "Off"
		},
		{
			value: 7,
			label: "1 week"
		},
		{
			value: 14,
			label: "2 weeks"
		},
		{
			value: 21,
			label: "3 weeks"
		}
	]

	// ---- Computed Properties ----

	public get userHasSetStatusPermission () : boolean {
		return !!(this.me.extendedPermissions &&
			this.me.extendedPermissions[ExtendedPermissions.SetOtherUsersStatus])
	}

	public get usersWithDisplayOptions () : UserWithDisplayOptions[] {
		if (!this.value) {
			return []
		}

		const userStatusIdsToFilter = this.value.userStatuses
			? this.value.userStatuses.map((c: UserStatus) => c.id)
			: []

		const allUsers: UserWithDisplayOptions[] = []

		for (let user of this.users) {
			const hasMatchingStatus =
				userStatusIdsToFilter.length === 0 ||
				(user.currentStatus && userStatusIdsToFilter.includes(user.currentStatus.id))

			const isInHighlightedTeam = this.userIsInHighlightedTeams(user)

			allUsers.push({
				user: user,
				hidden: !(hasMatchingStatus || isInHighlightedTeam) && !this.value.showHiddenAsMuted,
				muted: !hasMatchingStatus && this.value.showHiddenAsMuted,
				isInHighlightedTeam: isInHighlightedTeam
			})
		}

		const sortedUsers = _.orderBy(allUsers, ["isInHighlightedTeam", "muted"], ["desc", "asc"])
		return sortedUsers
	}

	private get statusesById (): { [key: string]: UserStatus } {
		return this.availableUserStatuses.toDictionary("id")
	}

	private get closeOnSelect ():boolean {
		return !(this.getIsFeatureEnabled(TenantFeatures.MultiSelectKeepOpen))
	}

	public get userStatusSchedulesEnabled ():boolean {
		return this.getIsFeatureEnabled(TenantFeatures.UserStatusSchedules)
	}

	// ---- Lifecycle Hooks ----

	public async mounted () {
		this.onEditChanged()
		await this.loadAvailableUserStatuses()
	}

	private gotoAccount (user: User) {
		this.$router.push({ path: `/userprofile/${user.id}` })
	}

	@Watch("edit")
	private async onEditChanged () {
		if (!this.edit) {
			this.loading = true

			if (this.value && this.value.teamId) {
				if (!this.activeTeam || this.activeTeam.id !== this.value.teamId) {
					if (this.teams) {
						this.activeTeam = this.teams.find(v => v.id === this.value.teamId)
					}
				}

				this.users = (await api.usersGet(this.value.teamId)).filter(user => !user.disabled)

				this.userSchedulesByUser = {}
				if (this.userStatusSchedulesEnabled && this.userHasSetStatusPermission && this.value && this.value.scheduleLookahead) {
					apiUserStatus.upcomingSchedules(this.value.scheduleLookahead, this.users.map(u => u.id)).then(schedules => {
						const scheduleRuns: UserStatusScheduleRun[] = schedules.flatMap(s => {
							const v = []

							const statusScheduleTimeZone = this.tenantTimeZones.find(tz => tz.id === s.timeZone)

							if (!statusScheduleTimeZone) throw new Error(`Time zone not found: ${s.timeZone}`)

							let startDateInLocalTime: string | null = null
							let endDateInLocalTime: string | null = null

							if (s.endScheduleId && s.startDate) {
								startDateInLocalTime = DateTime.fromISO(s.startDate, { setZone: true })
									.setZone(statusScheduleTimeZone.iana, { keepLocalTime: true })
									.toLocal().toISO()
							}
							if (s.endScheduleId && s.endDate) {
								endDateInLocalTime = DateTime.fromISO(s.endDate, { setZone: true })
									.setZone(statusScheduleTimeZone.iana, { keepLocalTime: true })
									.toLocal().toISO()
							}

							v.push({
								id: s.id!,
								userId: s.userId!,
								startDate: startDateInLocalTime,
								endDate: endDateInLocalTime,
								status: s.startStatusId
							})

							return v
						}).sortBy("startDate")
						this.userSchedulesByUser = scheduleRuns.groupBy("userId")
					})
				}
			}

			this.loading = false
		}
	}

	private async userStatusChanged (userId: string, userStatus: UserStatus) {
		if (userStatus) {
			await (userId === this.me.id
				? this.setCurrentUserStatus(userStatus)
				: api.setUserStatus(userId, userStatus.id!))
		} else {
			await (userId === this.me.id
				? this.clearCurrentUserStatus()
				: api.clearUserStatus(userId))
		}
	}

	private userIsInHighlightedTeams (user: User) : boolean {
		if (!this.value ||
			!this.value.highlightedTeamIds ||
			this.value.highlightedTeamIds.length === 0 ||
			!user.teams) {
			return false
		}

		const userTeamIds = user.teams.map(c => c.id) as number[]
		const highlightedTeamIds = this.value.highlightedTeamIds as number[]

		return userTeamIds.some(userTeamId => highlightedTeamIds.includes(userTeamId))
	}
}

interface UserWithDisplayOptions {
	user: User,
	hidden: boolean,
	muted: boolean,
	isInHighlightedTeam: boolean
}
